import React, { useRef, useEffect } from 'react';
import classNames from 'classnames';

import CoreLink from './core_link';
import { isEmpty } from 'lodash';
import { MParticleEvent } from '../elog/mparticle_types';
import { RCImage } from '@reverbdotcom/cadence/components';

export interface IJumplink {
  text: string | JSX.Element;
  id?: string;
  inSidebar?: boolean;
  isActive?: boolean;
  clickEvent?: MParticleEvent;
}

interface IProps {
  jumplinks?: IJumplink[];
  centered?: boolean;
  image?: string | null;
}

function makeId(jump: IJumplink) {
  if (jump.id) return jump.id;

  return jump.text.toString().toLowerCase().replace(/[\W\b\s]/gi, '');
}

export default function Jumplinks({ jumplinks, centered, image }: IProps) {
  const jumplinksContainerRef = useRef<HTMLUListElement>(null);
  const activeJumplinkRef = useRef<HTMLLIElement>(null);

  // Scroll the active jumplink into view
  useEffect(() => {
    const container = jumplinksContainerRef.current;
    const activeLink = activeJumplinkRef.current;

    if (!container || !activeLink) return;

    const activeLinkOffset = activeLink.offsetLeft - container.offsetLeft;
    const availableSpace = container.offsetWidth - activeLink.offsetWidth;
    const scrollLeftPosition = activeLinkOffset - availableSpace;

    container.scrollTo({ left: scrollLeftPosition, behavior: 'auto' });
  }, [jumplinks]);

  if (isEmpty(jumplinks)) return null;

  return (
    <nav className={classNames('jumplinks', { 'jumplinks--centered': centered })}>
      <div className={classNames({ 'jumplinks--with-image-container': image })}>
        {image && (
          <div className="jumplinks__image">
            <RCImage
              src={image}
              alt=""
              imageBox="auto"
              overlay="offwhite"
              borderRadius="md"
            />
          </div>
        )}
        <ul className="jumplinks__inner" ref={jumplinksContainerRef}>
          {jumplinks.map((jump) => {
            const id = makeId(jump);

            return (
              <li
                key={id}
                ref={jump.isActive ? activeJumplinkRef : null}
                className={classNames('jumplink-item', {
                  'jumplink-item--in-sidebar': jump.inSidebar,
                })}
              >
                <CoreLink
                  key={id}
                  to={{ anchor: id }}
                  className={classNames('jumplink', `jumplink--${id}`, {
                    'jumplink--active': jump.isActive,
                  })}
                  clickEvent={jump.clickEvent}
                  replace
                >
                  {jump.text}
                </CoreLink>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
